import { CountryEnum } from '@weinvest/types/src/enums/country.enum';
import { LocaleEnum } from '@weinvest/types/src/enums/locale.enum';

type TSupportedLocale = 'frFr' | 'frBe' | 'nlBe' | 'enBe';
enum SanitizedIso4Enum {
	FR_FR = 'frfr',
	FR_BE = 'frbe',
	NL_BE = 'nlbe',
	EN_BE = 'enbe',
}

/** One per reader locale — `Intl.DisplayNames` resolves display data on construction. */
const languageNamesByViewer = new Map<string, Intl.DisplayNames>();

export class LocaleUtil {
	static sanitizedIso4 = (iso4: string): string => iso4.replace(/[^a-z^A-Z]/g, '').toLowerCase();

	/** The catalogue key, or `undefined` — `LocaleEnum` has eleven members and the catalogue names four. */
	static tryGetCamelCaseIso4 = (iso4: string): TSupportedLocale | undefined => {
		switch (this.sanitizedIso4(iso4)) {
			case SanitizedIso4Enum.FR_FR:
				return 'frFr';
			case SanitizedIso4Enum.EN_BE:
				return 'enBe';
			case SanitizedIso4Enum.NL_BE:
				return 'nlBe';
			case SanitizedIso4Enum.FR_BE:
				return 'frBe';
			default:
				return undefined;
		}
	};

	static getCamelCaseIso4 = (iso4: string): TSupportedLocale => this.tryGetCamelCaseIso4(iso4) ?? 'frBe';

	/**
	 * The language a person is addressed in, named in the reader's own — « Français » to a French
	 * speaker, « Frans » to a Dutch one. Names all eleven `LocaleEnum` members, so none prints raw.
	 *
	 * Only the language subtag is looked up, never the full tag. `Intl` reads `nl-BE` as "flamand"
	 * and `fr-BE` as "français (Belgique)" — canonical, and not what anyone here says; `nl` gives
	 * "Néerlandais", which is both. It also collapses `FR_FR` and `FR_BE` onto one label, which is
	 * what the catalogue this replaces did by giving the two of them the same string.
	 *
	 * Capitalised rather than trusted: French lowercases language names and Dutch capitalises them,
	 * so fixing the first character is what reproduces both catalogues without holding either.
	 */
	static languageLabel = (iso4: string, viewerLocale: string): string => {
		let names = languageNamesByViewer.get(viewerLocale);

		if (!names) {
			names = new Intl.DisplayNames([viewerLocale], { type: 'language' });
			languageNamesByViewer.set(viewerLocale, names);
		}

		const name = names.of(iso4.split(/[_-]/)[0].toLowerCase());

		return name ? name.charAt(0).toUpperCase() + name.slice(1) : iso4;
	};

	static getIso2LocaleBasedOnIso4 = (iso4: string): 'en' | 'nl' | 'fr' => {
		const sanitizedIso4 = this.sanitizedIso4(iso4);

		switch (sanitizedIso4) {
			case SanitizedIso4Enum.EN_BE:
				return 'en';
			case SanitizedIso4Enum.NL_BE:
				return 'nl';
			case SanitizedIso4Enum.FR_BE:
			case SanitizedIso4Enum.FR_FR:
			default:
				return 'fr';
		}
	};

	static getLocaleEnumBasedOnIso4 = (iso4: string): LocaleEnum => {
		const sanitizedIso4 = this.sanitizedIso4(iso4);

		switch (sanitizedIso4) {
			case SanitizedIso4Enum.FR_FR:
				return LocaleEnum.FR_FR;
			case SanitizedIso4Enum.FR_BE:
				return LocaleEnum.FR_BE;
			case SanitizedIso4Enum.NL_BE:
				return LocaleEnum.NL_BE;
			case SanitizedIso4Enum.EN_BE:
				return LocaleEnum.EN_BE;
			default:
				return LocaleEnum.FR_BE;
		}
	};

	static getCountryBasedOnIso4 = (iso4: string): CountryEnum => {
		const sanitizedIso4 = this.sanitizedIso4(iso4);

		switch (sanitizedIso4) {
			case SanitizedIso4Enum.FR_FR:
				return CountryEnum.FR;
			case SanitizedIso4Enum.FR_BE:
			case SanitizedIso4Enum.NL_BE:
			case SanitizedIso4Enum.EN_BE:
			default:
				return CountryEnum.BE;
		}
	};

	/**
	 * Order first the main office country then country alphabetically then name alphabetically
	 * @param languages Array of language with at least country and name attribute
	 * @param mainCountry First country of the ordered array
	 * @returns Ordered array
	 */
	static orderLocaleBasedOnCountry = <T>(
		languages: ({ country: string; name: string } & T)[],
		mainCountry?: string,
	): typeof languages => {
		return languages.sort((a, b) => {
			if (a.country === mainCountry && b.country !== mainCountry) {
				return -1;
			}

			if (a.country !== mainCountry && b.country === mainCountry) {
				return 1;
			}

			return a.country.localeCompare(b.country) || a.name.localeCompare(b.name);
		});
	};
}
